import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useSite } from "../context/SiteContext";
import { useWipe } from "../components/PageWipe";
import HeroMedia from "../components/HeroMedia";
import { PlusButton } from "../components/PlusButton";
import { Reveal } from "../components/Reveal";
import FieldLines from "../components/FieldLines";

const EASE = [0.16, 1, 0.3, 1];

export default function ProjectDetail() {
    const { id } = useParams();
    const { site, tx } = useSite();
    const wipeTo = useWipe();

    const projects = (site?.projects || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = projects.findIndex((p) => p.id === id);
    const p = idx >= 0 ? projects[idx] : null;
    const next = projects.length > 1 ? projects[(idx + 1) % projects.length] : null;

    useEffect(() => {
        if (p) document.title = `${tx(p.name)} — Beyond The Field+`;
    }, [p, tx]);

    if (!site) {
        return (
            <div className="flex h-screen items-center justify-center bg-[#0A0A0A]" data-testid="site-loader">
                <span className="font-display animate-pulse text-6xl text-[#D60024]">+</span>
            </div>
        );
    }

    if (!p) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-[#0A0A0A] text-white" data-testid="project-not-found">
                <h1 className="font-display text-6xl">{tx({ en: "PROJECT NOT FOUND", pt: "PROJETO NÃO ENCONTRADO" })}</h1>
                <button onClick={() => wipeTo("/", "#projects")} data-testid="project-back-link" className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D60024] u-line">
                    ← {tx({ en: "Back to projects", pt: "Voltar aos projetos" })}
                </button>
            </div>
        );
    }

    return (
        <main className="min-h-screen bg-[#0A0A0A] text-white" data-testid="project-detail-page">
            <header className="fixed inset-x-0 top-0 z-40 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
                <div className="flex items-center justify-between px-6 py-4 md:px-12">
                    <button onClick={() => wipeTo("/")} data-testid="detail-logo" className="font-display text-left leading-[0.8] text-white">
                        <span className="block text-lg tracking-wide">BEYOND</span>
                        <span className="block text-lg tracking-wide">THE FIELD<span className="text-[#D60024]">+</span></span>
                    </button>
                    <button onClick={() => wipeTo("/", "#projects")} data-testid="project-back-link" className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70 transition-colors hover:text-[#D60024]">
                        ← {tx({ en: "All projects", pt: "Todos os projetos" })}
                    </button>
                </div>
            </header>

            <section className="relative overflow-hidden px-6 pb-16 pt-36 md:px-12 md:pt-44 lg:px-24">
                <FieldLines className="absolute -right-56 -top-24 h-[36rem] w-[36rem] opacity-60" />
                <div className="relative">
                    <div className="overflow-hidden">
                        <motion.p
                            initial={{ y: "120%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                            className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#D60024]"
                            data-testid="project-detail-overline"
                        >
                            {tx({ en: "Project", pt: "Projeto" })} — {String(idx + 1).padStart(2, "0")} / {p.year}
                        </motion.p>
                    </div>
                    <h1 className="font-display mt-6 leading-[0.85] tracking-tight">
                        <span className="block overflow-hidden">
                            <motion.span
                                initial={{ y: "112%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1.05, delay: 0.4, ease: EASE }}
                                className="block text-[clamp(3.5rem,10vw,9rem)]"
                                data-testid="project-detail-title"
                            >
                                {tx(p.name)}
                            </motion.span>
                        </span>
                    </h1>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                        className="mt-10 flex flex-wrap gap-x-14 gap-y-4 border-t border-white/15 pt-6"
                        data-testid="project-detail-meta"
                    >
                        {[
                            { label: tx({ en: "Client", pt: "Cliente" }), value: p.client },
                            { label: tx({ en: "Category", pt: "Categoria" }), value: tx(p.category) },
                            { label: tx({ en: "Year", pt: "Ano" }), value: p.year },
                        ].filter((m) => m.value).map((m) => (
                            <div key={m.label}>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40">{m.label}</p>
                                <p className="mt-1.5 text-sm font-medium">{m.value}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24">
                <Reveal>
                    <div className="relative aspect-video w-full overflow-hidden bg-black" data-testid="project-detail-media">
                        {p.video_url ? (
                            <HeroMedia url={p.video_url} poster={p.image_url} autoplay={false} className="absolute inset-0 h-full w-full object-cover" />
                        ) : (
                            <img src={p.image_url} alt={tx(p.name)} className="h-full w-full object-cover" />
                        )}
                    </div>
                </Reveal>
            </section>

            <section className="grid gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32 lg:px-24">
                <div className="md:col-span-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#D60024]">{tx({ en: "About the project", pt: "Sobre o projeto" })}</p>
                </div>
                <div className="md:col-span-6">
                    <Reveal>
                        <p className="text-lg leading-relaxed text-white/80" data-testid="project-detail-description">{tx(p.description)}</p>
                    </Reveal>
                    {p.url && (
                        <Reveal delay={0.1}>
                            <div className="mt-10">
                                <PlusButton href={p.url} label={tx({ en: "VISIT PROJECT", pt: "VISITAR PROJETO" })} testId="project-visit-btn" />
                            </div>
                        </Reveal>
                    )}
                </div>
            </section>

            {next && (
                <button onClick={() => wipeTo(`/projects/${next.id}`)} data-testid="project-next-link" className="group block w-full border-t border-white/10 px-6 py-20 text-left md:px-12 lg:px-24">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/40">{tx({ en: "Next project", pt: "Próximo projeto" })}</p>
                    <span className="font-display text-outline-w mt-4 block text-[clamp(2.8rem,8vw,7rem)] leading-[0.85] transition-colors duration-500 group-hover:text-white">
                        {tx(next.name)}
                        <span className="ml-4 inline-block text-[#D60024] transition-transform duration-300 group-hover:rotate-45">+</span>
                    </span>
                </button>
            )}
        </main>
    );
}
